import React, { useMemo } from 'react';
import DOMPurify from 'isomorphic-dompurify';
import { useDCAPageModelStore } from '@marriott/mi-store-utils';
import { extractUniversalFooterModelFromPageModel } from '../../utils/extractUniversalFooterModel';
import type { FooterNavItem } from '../../molecules/MConversationalNavGroupVertical/MConversationalNavGroupVertical.types';

const sanitizeFooterHtml = (html: string) => (DOMPurify.sanitize(html, { RETURN_DOM: true }) as HTMLElement).outerHTML;

/** Keeps hosts in DOM (not display:none) so CMP scripts can bind on load; visually hidden. */
const visuallyHidden: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
};

/**
 * Renders universal-footer `privacyItems[].htmlContent` in the document as soon as the sidebar
 * is shown so OneTrust / similar scripts can attach on first init. Settings overlay rows omit
 * duplicate markup and forward clicks instead (see MConversationalNavGroupVertical).
 */
export const HiddenPrivacyFooterHosts: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pageModel = useDCAPageModelStore(state => state.pageModel as any);

  const htmlChunks = useMemo(() => {
    const footer = extractUniversalFooterModelFromPageModel(pageModel);
    const items = (footer?.privacyItems ?? []) as FooterNavItem[];
    return items.map(i => i.htmlContent).filter((h): h is string => Boolean(h && String(h).trim()));
  }, [pageModel]);

  if (!htmlChunks.length) return null;

  return (
    <div aria-hidden="true" data-testid="hidden-privacy-footer-hosts" style={visuallyHidden}>
      {htmlChunks.map((html, index) => (
        <div key={index} dangerouslySetInnerHTML={{ __html: sanitizeFooterHtml(html) }} />
      ))}
    </div>
  );
};
